import React, { useEffect, useMemo, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { createOrder, getDeliverySlots, getProducts } from '../lib/api';
import { getTelegramUser } from '../lib/telegram';
import { useCartStore } from '../state/cart.store';
import type { Product } from '../types';

type DeliveryMode = 'delivery' | 'meetup';
type FieldErrors = Partial<Record<'telegramUsername' | 'deliverySlot' | 'zone', string>>;

const FALLBACK_SLOTS = ['11/12', '12/13', '13/14', '14/15', '15/16', '16/17', '17/18', '18/19'].map(time => ({ time, available: true }));

const Checkout: React.FC = () => {
    const history = useHistory();
    const { items, clearCart } = useCartStore();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [products, setProducts] = useState<Product[]>([]);
    const [telegramUsername, setTelegramUsername] = useState('');
    const [deliveryMode, setDeliveryMode] = useState<DeliveryMode>('delivery');
    const [zone, setZone] = useState('');
    const [deliverySlots, setDeliverySlots] = useState<{ time: string; available: boolean; remaining?: number }[]>([]);
    const [deliverySlot, setDeliverySlot] = useState('');
    const [note, setNote] = useState('');
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
    const [success, setSuccess] = useState<null | {
        orderId: string;
        total: number;
        deliverySlot: string;
    }>(null);
    const [successItems, setSuccessItems] = useState<typeof items>([]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const data = await getProducts();
                if (!cancelled) setProducts(data);
            } catch {
                // ignore
            }
        })();
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        const tgUser = getTelegramUser();
        if (tgUser?.username) {
            setTelegramUsername(tgUser.username);
        }
    }, []);

    const loadSlots = async (opts?: { keepSelection?: boolean }) => {
        try {
            const data = await getDeliverySlots();
            const slots = data.slots ?? [];
            setDeliverySlots(slots);
            if (!opts?.keepSelection) {
                const firstAvailable = slots.find(s => s.available)?.time;
                setDeliverySlot(firstAvailable ?? '');
            }
        } catch {
            setDeliverySlots(FALLBACK_SLOTS);
            if (!opts?.keepSelection && !deliverySlot) {
                setDeliverySlot(FALLBACK_SLOTS[0]?.time ?? '');
            }
        }
    };

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (cancelled) return;
            await loadSlots();
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!success && items.length === 0) {
            history.replace('/');
        }
    }, [history, items.length, success]);

    const total = useMemo(() => {
        return items.reduce((sum, item) => {
            const product = products.find(p => p.id === item.productId);
            return sum + (product?.price ?? 0) * item.quantity;
        }, 0);
    }, [items, products]);

    const handleCheckout = async () => {
        const nextFieldErrors: FieldErrors = {};
        if (telegramUsername.trim().length < 3) nextFieldErrors.telegramUsername = 'Nome Telegram obbligatorio (es. @mario_rossi)';
        if (deliveryMode === 'delivery' && zone.trim().length < 3) nextFieldErrors.zone = 'Inserisci la tua zona e via';
        if (deliverySlot.trim().length < 4) nextFieldErrors.deliverySlot = 'Seleziona una fascia oraria';
        setFieldErrors(nextFieldErrors);
        if (Object.keys(nextFieldErrors).length > 0) {
            setError('Controlla i campi obbligatori.');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const tgUser = getTelegramUser();
            const snapshotItems = items.slice();

            const orderData = {
                items,
                customerName: telegramUsername.trim(),
                customerAddress: deliveryMode === 'delivery' ? zone : 'MEET-UP',
                note: note.trim() || undefined,
                telegramUserId: tgUser?.id ? String(tgUser.id) : undefined,
                telegramUsername: telegramUsername.trim(),
                deliverySlot: deliverySlot.trim(),
                deliveryMode,
            };

            const result = await createOrder(orderData);
            setSuccessItems(snapshotItems);
            setSuccess(result);
            clearCart();
        } catch (err) {
            const anyErr = err as any;
            setError(anyErr?.message ?? 'Impossibile inviare l\'ordine. Riprova tra poco.');
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="checkout">
                <h2>Ordine inviato!</h2>

                <div className="success-message">
                    <p>
                        Grazie per il tuo ordine! Un nostro operatore ti contatterà su Telegram per confermare i dettagli e la consegna.
                    </p>
                </div>

                <p className="muted">Codice ordine: <strong>{success.orderId}</strong></p>
                <p className="muted">Fascia consegna: <strong>{success.deliverySlot}</strong></p>

                <h3>Riepilogo</h3>
                <ul>
                    {successItems.map(item => {
                        const product = products.find(p => p.id === item.productId);
                        const title = product
                            ? product.variantLabel
                                ? `${product.name} (${product.variantLabel})`
                                : product.name
                            : `Prodotto ${item.productId}`;
                        return (
                            <li key={item.productId}>
                                {title} x{item.quantity}
                            </li>
                        );
                    })}
                </ul>

                <p>
                    <strong>Totale:</strong> €{success.total.toFixed(2)}
                </p>

                <div className="actions">
                    <Link className="checkout-button" to="/">
                        Torna alla home
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="checkout">
            <h2>Checkout</h2>
            <p>
                <Link to="/cart">← Torna al carrello</Link>
            </p>

            <div className="info-box">
                <p>Pagamento <strong>alla consegna</strong>. Dopo l'ordine verrai ricontattato da un nostro operatore su Telegram per confermare consegna e dettagli.</p>
            </div>

            {error && <p className="error">{error}</p>}

            <ul>
                {items.map(item => {
                    const product = products.find(p => p.id === item.productId);
                    const title = product
                        ? product.variantLabel
                            ? `${product.name} (${product.variantLabel})`
                            : product.name
                        : `Prodotto ${item.productId}`;
                    return (
                        <li key={item.productId}>
                            {title} x{item.quantity}
                        </li>
                    );
                })}
            </ul>
            <p>
                <strong>Totale:</strong> €{total.toFixed(2)}
            </p>

            <div className="checkout-form">
                <label>
                    Nome Telegram
                    <input
                        value={telegramUsername}
                        onChange={e => setTelegramUsername(e.target.value)}
                        placeholder="@mario_rossi"
                    />
                    {fieldErrors.telegramUsername ? <span className="field-error">{fieldErrors.telegramUsername}</span> : null}
                </label>
                <label>
                    Modalità di ritiro
                    <select value={deliveryMode} onChange={e => setDeliveryMode(e.target.value as DeliveryMode)}>
                        <option value="delivery">Delivery (consegna a domicilio)</option>
                        <option value="meetup">Meet-up (ritiro di persona)</option>
                    </select>
                </label>
                {deliveryMode === 'delivery' && (
                    <label>
                        Zona / Via
                        <input
                            value={zone}
                            onChange={e => setZone(e.target.value)}
                            placeholder="Es. Via Indipendenza 10, Bologna"
                        />
                        {fieldErrors.zone ? <span className="field-error">{fieldErrors.zone}</span> : null}
                    </label>
                )}
                <label>
                    Fascia oraria consegna
                    <select value={deliverySlot} onChange={e => setDeliverySlot(e.target.value)}>
                        <option value="">Seleziona un orario</option>
                        {deliverySlots.map(s => (
                            <option key={s.time} value={s.time} disabled={!s.available}>
                                {s.time}{!s.available ? ' — Esaurita' : s.remaining != null ? ` (${s.remaining} posti)` : ''}
                            </option>
                        ))}
                    </select>
                    {fieldErrors.deliverySlot ? <span className="field-error">{fieldErrors.deliverySlot}</span> : null}
                </label>
                <label>
                    Note (opzionale)
                    <input value={note} onChange={e => setNote(e.target.value)} placeholder="Citofono, interno, altro..." />
                </label>
            </div>
            <button onClick={handleCheckout} disabled={loading}>
                {loading ? 'Invio in corso...' : 'Invia ordine'}
            </button>
        </div>
    );
};

export default Checkout;
